import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { PriceListApiService } from '../services/price-list-api.service';
import { ProductoApiService } from '../services/producto-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import {
  PriceListResponse,
  PriceListSearchParams,
} from '../models/price-list.model';
import { CurrencyResponse } from '../models/producto.model';
import { PriceListDetailModalComponent } from '../price-list-detail-modal/price-list-detail-modal.component';
import { ConfirmDialogService } from '../../../shared/services/confirm-dialog.service';
import { ToastService } from '../../../shared/services/toast.service';

type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

@Component({
  selector: 'app-price-list-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    PageBreadcrumbComponent,
    BadgeComponent,
    PriceListDetailModalComponent,
  ],
  templateUrl: './price-list-list.component.html',
})
export class PriceListListComponent implements OnInit {
  priceLists: PriceListResponse[] = [];
  currencies: CurrencyResponse[] = [];
  loading = false;
  error = '';
  businessId = 1;

  showDetailModal = false;
  selectedPriceList: PriceListResponse | null = null;
  togglingId: number | null = null;

  // Filters
  showFilters = false;
  filterName = '';
  filterIsDefault: boolean | undefined;
  filterCurrencyId: number | undefined;
  filterIsActive: boolean | undefined;

  constructor(
    private priceListApi: PriceListApiService,
    private productApi: ProductoApiService,
    private authService: AuthService,
    private router: Router,
    private confirmDialog: ConfirmDialogService,
    private toast: ToastService,
  ) {
    const user = this.authService.getCurrentUser();
    this.businessId = user?.businessId || 1;
  }

  ngOnInit(): void {
    this.cargarMonedas();
    this.cargarListas();
  }

  private cargarMonedas(): void {
    this.productApi.getCurrencies().subscribe({
      next: (data) => (this.currencies = data),
      error: () => {},
    });
  }

  cargarListas(): void {
    this.loading = true;
    this.error = '';
    const req: PriceListSearchParams = {
      businessId: this.businessId,
      searchName: this.filterName || undefined,
      isDefault: this.filterIsDefault,
      currencyId: this.filterCurrencyId || undefined,
      activeOnly: this.filterIsActive,
    };
    this.priceListApi.searchPriceList(req).subscribe({
      next: (data) => {
        this.priceLists = data;
        this.loading = false;
      },
      error: (err) => {
        this.priceLists = [];
        this.loading = false;
        this.error = err.error?.message || 'Error al cargar listas';
      },
    });
  }

  buscar(): void {
    this.cargarListas();
  }

  limpiarFiltro(): void {
    this.filterName = '';
    this.filterIsDefault = undefined;
    this.filterCurrencyId = undefined;
    this.filterIsActive = undefined;
    this.cargarListas();
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  nuevaLista(): void {
    this.router.navigate(['/productos/listas-precios/crear']);
  }

  editarLista(list: PriceListResponse): void {
    this.router.navigate(['/productos/listas-precios/editar', list.id]);
  }

  verDetalle(list: PriceListResponse): void {
    this.selectedPriceList = list;
    this.showDetailModal = true;
  }

  cerrarModal(): void {
    this.showDetailModal = false;
    this.selectedPriceList = null;
  }

  async eliminarLista(list: PriceListResponse): Promise<void> {
    if (list.isDefault) {
      await this.confirmDialog.alert({
        title: 'Aviso',
        message: 'No se puede eliminar la lista por defecto',
        variant: 'warning',
      });
      return;
    }
    const ok = await this.confirmDialog.confirmDanger({
      title: 'Eliminar lista',
      message: `¿Eliminar la lista "${list.name}"?`,
    });
    if (!ok) return;
    this.priceListApi.deletePriceList(list.id, this.businessId).subscribe({
      next: () => {
        this.cargarListas();
        this.toast.deleted('Lista de precios');
      },
      error: (err) => {
        this.error = err.error?.message || 'Error al eliminar';
        this.toast.error({
          title: 'Error',
          message: err.error?.message || 'Error al eliminar',
        });
      },
    });
  }

  getCurrencyCode(currencyId?: number): string {
    if (!currencyId) return '-';
    const c = this.currencies.find((x) => x.id === currencyId);
    return c?.code || `#${currencyId}`;
  }

  getDefaultColor(list: PriceListResponse): BadgeColor {
    return list.isDefault ? 'primary' : 'light';
  }

  //boton para activar/desactivar
  async toggleActivo(list: PriceListResponse): Promise<void> {
    if (list.isDefault) {
      await this.confirmDialog.alert({
        title: 'Aviso',
        message: 'La lista por defecto no se puede desactivar',
        variant: 'warning',
      });
      return;
    }

    const nuevoEstado = !list.isActive;
    const accion = nuevoEstado ? 'activar' : 'desactivar';
    if (accion === 'activar') {
      const ok = await this.confirmDialog.confirm({
        title: `${nuevoEstado ? 'Activar' : 'Desactivar'} lista`,
        message: `¿Desea ${accion} la lista "${list.name}"?`,
        confirmText: 'Activar',
        variant: 'success',
      });
      if (!ok) return;
    }
    if (accion === 'desactivar') {
      const ok = await this.confirmDialog.confirm({
        title: `${nuevoEstado ? 'Activar' : 'Desactivar'} lista`,
        message: `¿Desea ${accion} la lista "${list.name}"?`,
        confirmText: 'Desactivar',
        variant: 'warning',
      });
      if (!ok) return;
    }

    this.togglingId = list.id;

    // Inc 51: usa el endpoint dedicado PATCH .../active para que el estado persista,
    // y refresca la lista para reflejar el estado real que devolvió el backend.
    this.priceListApi
      .setPriceListActive(list.id, nuevoEstado, this.businessId)
      .subscribe({
        next: (updated) => {
          list.isActive = updated.isActive ?? nuevoEstado;
          this.togglingId = null;
          this.toast.success({
            title: 'Listo',
            message: `Lista ${nuevoEstado ? 'activada' : 'desactivada'}`,
          });
          this.cargarListas();
        },
        error: (err) => {
          this.togglingId = null;
          this.toast.error({
            title: 'Error',
            message: err.error?.message || `No se pudo ${accion} la lista`,
          });
        },
      });
  }
}
